<!-- eslint-disable vue/no-bare-strings-in-template, vue/no-restricted-html-elements -->
<template>
  <div class="site-footer">
    <div class="site-footer__container container">
      <nav class="site-footer__navigation-container">
        <div
          v-for="(tier1, index) of footerMenu"
          :key="`${tier1.title}-${index}`"
          class="site-footer__navigation-list-item"
        >
          <p
            class="site-footer__navigation-title text-heading-l"
            v-text="tier1.title"
          />

          <ul
            class="site-footer__navigation-list"
          >
            <li
              v-for="(tier2, index2) of tier1.links"
              :key="`${tier2.title}-${index2}`"
            >
              <a
                class="site-footer__navigation-link text-body-s-regular"
                :href="tier2.url"
                v-text="tier2.title"
              />
            </li>
          </ul>
        </div>
      </nav>

      <div class="site-footer__newsletter-container">
        <p
          class="site-footer__newsletter-heading text-heading-l"
          v-text="$string('general.site_footer.newsletter_heading')"
        />

        <p
          class="site-footer__newsletter-text text-body-s-regular"
          v-text="$string('general.site_footer.newsletter_text')"
        />

        <p
          v-if="state === 'success'"
          class="site-footer__newsletter-success text-body-l-bold"
          v-text="$string('general.site_footer.newsletter_success')"
        />

        <form
          v-else
          class="site-footer__newsletter__input-container"
          @submit.prevent="handleNewsletterSubmit"
        >
          <label
            htmlFor="newsletter-email"
            class="site-footer__newsletter__input-label visually-hidden"
            v-text="$string('general.site_footer.newsletter_placeholder')"
          />

          <input
            id="newsletter-email"
            v-model="email"
            type="email"
            class="site-footer__newsletter__input text-body-s-regular"
            :placeholder="$string('general.site_footer.newsletter_placeholder')"
            required
          >

          <btn
            class="site-footer__newsletter-btn"
            :label="$string('general.site_footer.newsletter_cta')"
            type="submit"
          />
        </form>

        <p
          v-if="state === 'error'"
          class="site-footer__newsletter-error text-body-s-regular"
          v-text="errorMessage"
        />

        <div class="site-footer__icons-container">
          <ul
            class="site-footer__social-container"
            :class="[
              `site-footer__social-container--${iconAlignment}`
            ]"
          >
            <template
              v-for="social of socialMedia"
              :key="social.name"
            >
              <li
                v-if="social.url.length"
                class="site-footer__social-icon"
              >
                <a
                  :href="social.url"
                  class="site-footer__social-link"
                  :title="$string('general.site_footer.social_platform', {
                    replace: {
                      name: $variable('store.shopName'),
                      platform: social.name
                    }
                  })
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <component
                    :is="`icon-${social.name}`"
                  />
                </a>
              </li>
            </template>
          </ul>

          <div
            v-if="certifiedIcon?.src.length"
            class="
              site-footer__certified
              is-hidden--mobile
            "
          >
            <img
              class="site-footer__certified-icon"
              :src="certifiedIcon?.src"
              :alt="$string('general.site_footer.certified_text')"
              :title="$string('general.site_footer.certified_text')"
              width="44"
            >
          </div>
        </div>
      </div>

      <ul class="site-footer__payment-container">
        <li
          v-for="icon of paymentIcons"
          :key="icon"
          class="site-footer__payment-icon"
          :title="icon"
        >
          <component :is="`icon-${icon}`" />
        </li>
      </ul>

      <div
        v-if="certifiedIcon?.src.length"
        class="
          site-footer__certified
          is-hidden--desktop
        "
      >
        <img
          class="site-footer__certified-icon"
          :src="certifiedIcon.src"
          :alt="$string('general.site_footer.certified_text')"
          :title="$string('general.site_footer.certified_text')"
          width="44"
        >
      </div>

      <div class="site-footer__legal-container">
        <p
          class="
            text-body-xs-regular
            text-body-s-regular-desktop
          "
          v-text="$string('general.site_footer.copyright', {
            replace: { year: currentYear }
          })"
        />

        <ul class="site-footer__legal-links">
          <li
            v-for="(link, index) of footerLegalMenu"
            :key="`${link.url}-${index}`"
          >
            <a
              class="
                site-footer__legal-link
                text-body-xs-regular
                text-body-s-regular-desktop
              "
              :href="link.url"
              v-text="link.title"
            />
          </li>
        </ul>
      </div>

      <country-selector
        class="site-footer__country-selector"
        :current-country="currentCountry"
        :current-language="currentLanguage"
        :countries="localizationCountries"
        :flags="flagIcons"
        :theme-custom-country-codes="themeCustomCountryCodes"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer-group)
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {Array} footerMenu - Navigation to be displayed in footer.
 * @param {Array} footerLegalMenu - Links to be displayed next to copyright
 * text.
 * @param {String} certifiedIcon - Alignment setting of social icons.
 * @param {Array} paymentIcons - Array of names of payment icons to be displayed
 * in footer.
 * @param {Object} flagIcons - Object of svg country flags.
 * @param {Array} socialMedia - Array of links and icons of social media
 * platforms to be displayed in footer.
 * @param {Object} iconAlignment - Image object of certified icon.
 * @param {String} currentCountry - Localization country ISO code.
 * @param {Array} localizationCountries - Array of localization countries.
 * @param {Array} customUsCountryCodes - Array of custom US country codes.
 * @param {Array} themeCustomCountryCodes - Array of custom US country codes.
 *
 */
import subscribe from '~/apis/klaviyo'

import Btn from '~global/btn/btn'
import CountrySelector from '~async/country-selector/country-selector'
import CountrySelectorUs from '~async/country-selector-us/country-selector-us'
import { isUSStore } from '~/helpers/general'

import IconAmex from '~icons/payment/amex.svg'
import IconApplePay from '~icons/payment/apple-pay.svg'
import IconGooglePay from '~icons/payment/google-pay.svg'
import IconKlarna from '~icons/payment/klarna.svg'
import IconMastercard from '~icons/payment/mastercard.svg'
import IconPaypal from '~icons/payment/paypal.svg'
import IconVisa from '~icons/payment/visa.svg'

import IconFacebook from '~icons/social/facebook.svg'
import IconInstagram from '~icons/social/instagram.svg'
import IconPinterest from '~icons/social/pinterest.svg'
import IconSnapchat from '~icons/social/snapchat.svg'
import IconTumblr from '~icons/social/tumblr.svg'
import IconTwitter from '~icons/social/twitter.svg'
import IconVimeo from '~icons/social/vimeo.svg'
import IconYoutube from '~icons/social/youtube.svg'
import IconTiktok from '~icons/social/tiktok.svg'

export default {
  name: 'SiteFooter',

  components: {
    Btn,
    CountrySelector,
    CountrySelectorUs,
    IconAmex,
    IconApplePay,
    IconGooglePay,
    IconKlarna,
    IconMastercard,
    IconPaypal,
    IconVisa,
    IconFacebook,
    IconInstagram,
    IconPinterest,
    IconSnapchat,
    IconTumblr,
    IconTwitter,
    IconVimeo,
    IconYoutube,
    IconTiktok,
  },

  props: {
    footerMenu: {
      type: Array,
      default: () => [],
    },

    footerLegalMenu: {
      type: Array,
      default: () => [],
    },

    certifiedIcon: {
      type: Object,
      default: () => ({}),
    },

    flagIcons: {
      type: Object,
      default: () => ({}),
    },

    paymentIcons: {
      type: Array,
      default: () => [],
    },

    socialMedia: {
      type: Array,
      default: () => [],
    },

    iconAlignment: {
      type: String,
      default: '',
    },

    currentCountry: {
      type: String,
      default: '',
    },

    currentLanguage: {
      type: String,
      default: '',
    },

    localizationCountries: {
      type: Array,
      default: () => [],
    },

    customUsCountryCodes: {
      type: Array,
      default: () => [],
    },

    themeCustomCountryCodes: {
      type: Array,
      default: () => [],
    },
  },

  data: () => {
    return {
      state: 'ready',
      email: '',
      errorMessage: '',
      isUS: isUSStore(),
    }
  },

  computed: {

    /**
     * Compute current year for copyright text items.
     * @returns {Number}
     */
    currentYear() {
      return new Date().getFullYear()
    },
  },

  methods: {

    /**
     * Handle newsletter form submission events.
     */
    handleNewsletterSubmit() {
      const customer = this.$variable('customer')
      const profile = {
        email: this.email,
        phone_number: customer.phone || null,
        first_name: customer.firstName || null,
        last_name: customer.lastName || null,
        location: customer.location || null,
      }

      subscribe('Footer Newsletter', profile)
        .then((response) => {
          if (!response.length) {
            this.state = 'success'
            return
          }

          const message = JSON.parse(response)
          if (message.errors) {
            this.state = 'error'
            this.errorMessage = message.errors[0]?.detail
          }
        })
        .catch((error) => {
          this.state = 'error'
          this.errorMessage = this.$string('general.site_footer.newsletter_error')
          reportError('Klaviyo subscription failed', error)
        })
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
